import { yupResolver } from '@hookform/resolvers/yup'
import { find, map } from 'lodash'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { getTranslation } from '../../../account/services/auth'
import CategoryIcon from '../../../assets/icons/category.svg'
import ColorIcon from '../../../assets/icons/color.svg'
import MaxNumberIcon from '../../../assets/icons/max-number.svg'
import TicketOrdinal from '../../../assets/icons/ordinal.svg'
import {
  Gradient,
  Languages,
  TicketCategory,
  TicketCategoryDetails,
  TicketCategoryPayload,
  Translations,
} from '../../../common/models'
import FormGroup from '../../../core/components/FormGroup'
import { formatDateTime } from '../../../core/constants/times'
import { getSettingsColors } from '../../../core/services/festival-settings'
import {
  createTicketCategory,
  getAllCategories,
  updateTicketCategory,
} from '../../../core/services/tickets'
import translate from '../../../core/services/translate'
import { ColorCircle } from '../ColorCircle'
import { MessageBox } from '../messages/MessageBox'
import { DateTimePicker } from '@/components/ui/date-time-picker'
import { Button, Checkbox, Input, Modal } from '@/components/ui'
import { PageTitle } from '@/components'
import SelectField from '@/components/select-field'
import { cn } from '@/lib/utils'

interface IValues {
  content?: Translations[]
  color?: string
  ordinal?: number
  published?: boolean
  publishDate?: string

  paymentOption?: 'BOTH' | 'FULL_PRICE' | 'MONTHLY'
  maxPerOrder?: number
  installment: {
    endDate?: string
    nrMaxPayments?: number | null
    firstInstallmentPercentage?: number | null
    firstInstallmentValue?: number | null
    feePercentage?: number | null
    feeValue?: number | null
  } | null
}

export interface TicketCategoryModalProps {
  item?: TicketCategoryDetails
  visible: boolean
  setVisible: (show: boolean) => void
  setTicketCategories: (c: TicketCategory[]) => void
  sortBy?: string
  currency: string
  allLanguages: Languages[]
}

const paymentOptions = [
  {
    name: 'BOTH',
    displayValue: 'BOTH',
  },
  {
    name: 'FULL_PRICE',
    displayValue: 'FULL_PRICE',
  },
  {
    name: 'MONTHLY',
    displayValue: 'MONTHLY',
  },
]

export const TicketCategoryModal = (
  props: TicketCategoryModalProps,
): JSX.Element => {
  const {
    visible,
    setVisible,
    item,
    setTicketCategories,
    sortBy,
    currency,
    allLanguages,
  } = props
  const language = getTranslation()
  const [colors, setColors] = useState<Gradient[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [isError, setIsError] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  const schema = yup.object().shape({
    content: yup
      .array()
      .min(1)
      .of(
        yup.object().shape({
          locale: yup.string().required(),
          name: yup.string().required(),
        }),
      ),
    color: yup.string(),
    ordinal: yup.number().transform((val) => (val ? Number(val) : 0)),
    published: yup.boolean(),
    publishDate: yup.string(),
    paymentOption: yup
      .mixed<'BOTH' | 'FULL_PRICE' | 'MONTHLY'>()
      .oneOf(['BOTH', 'FULL_PRICE', 'MONTHLY']),

    maxPerOrder: yup
      .number()
      .optional()
      .transform((value) => (value ? Number(value) : 0)),
    installment: yup
      .object()
      .shape({
        endDate: yup.string().test('endDate', 'required', function () {
          const { from, originalValue } = this
          const paymentOption = from?.[1]?.value.paymentOption
          const nrMaxPayments = from?.[1]?.value?.installment?.nrMaxPayments
          if (paymentOption === 'FULL_PRICE') {
            return true
          }
          return originalValue || nrMaxPayments
        }),
        nrMaxPayments: yup
          .number()
          .test('nrMaxPayments', 'required', function () {
            const { from, originalValue } = this
            const paymentOption = from?.[1]?.value.paymentOption
            const endDate = from?.[1]?.value?.installment?.endDate
            if (paymentOption === 'FULL_PRICE') {
              return true
            }
            return endDate || originalValue
          }),
        firstInstallmentPercentage: yup
          .number()
          .test('firstInstallmentPercentage', 'required', function () {
            const { from, originalValue } = this
            const paymentOption = from?.[1]?.value.paymentOption
            const firstInstallmentValue =
              from?.[1]?.value?.installment?.firstInstallmentValue
            if (paymentOption === 'FULL_PRICE') {
              return true
            }
            return originalValue || firstInstallmentValue
          })
          .transform((value) => (value ? Number(value) : 0)),
        firstInstallmentValue: yup
          .number()
          .test('firstInstallmentValue', 'required', function () {
            const { from, originalValue } = this
            const paymentOption = from?.[1]?.value.paymentOption
            const firstInstallmentPercentage =
              from?.[1]?.value?.installment?.firstInstallmentPercentage
            if (paymentOption === 'FULL_PRICE') {
              return true
            }

            return originalValue || firstInstallmentPercentage > 0
          })
          .transform((value) => (value ? Number(value) : 0)),
        feePercentage: yup
          .number()
          .test('feePercentage', 'required', function () {
            const { from, originalValue } = this
            const paymentOption = from?.[1]?.value.paymentOption
            const feeValue = from?.[1]?.value?.installment?.feeValue
            if (paymentOption === 'FULL_PRICE') {
              return true
            }
            return originalValue || feeValue
          })
          .transform((value) => (value ? Number(value) : 0)),
        feeValue: yup
          .number()
          .test('feeValue', 'required', function () {
            const { from, originalValue } = this
            const paymentOption = from?.[1]?.value.paymentOption
            const feePercentage = from?.[1]?.value?.installment?.feePercentage
            if (paymentOption === 'FULL_PRICE') {
              return true
            }
            return originalValue || feePercentage
          })
          .transform((value) => (value ? Number(value) : 0)),
      })
      .when('paymentOption', {
        is: (val: 'MONTHLY' | 'FULL_PRICE' | 'BOTH') => val === 'FULL_PRICE',
        then: (schema) => schema.nullable().optional(),
        otherwise: (schema) => schema.required(),
      }),
  })

  const {
    handleSubmit,
    control,
    register,
    reset,
    setValue,
    watch,
    formState: { errors, dirtyFields },
  } = useForm<IValues>({
    mode: 'onChange',
    resolver: yupResolver<IValues>(schema),
    defaultValues: {
      color: undefined,
      ordinal: undefined,
      published: false,
      publishDate: undefined,
      paymentOption: 'BOTH',
      maxPerOrder: undefined,
      installment: { nrMaxPayments: 0 },
    },
  })

  const handleClose = () => {
    reset()
    setVisible(false)
  }

  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        setLoading(true)
        const resp = await getSettingsColors()
        const main = find(resp.data, (data) => data.name === 'PRIMARY')
        const secondary = find(resp.data, (data) => data.name === 'SECONDARY')
        const ternary = find(resp.data, (data) => data.name === 'TERTIARY')
        const temp: Gradient[] = []
        if (main?.gradients) {
          temp.push(...main.gradients)
        }
        if (secondary?.gradients) {
          temp.push(...secondary.gradients)
        }
        if (ternary?.gradients) {
          temp.push(...ternary.gradients)
        }

        setColors(temp)
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  const applyInstallmentDefaults = useCallback(
    (
      installment?: TicketCategoryDetails['paymentsConfig']['installmentConfig'],
    ) => {
      if (!installment) {
        return
      }
      if (installment.installmentEndDate) {
        setValue(
          'installment.endDate',
          formatDateTime(installment.installmentEndDate),
        )
      }
      setValue('installment.nrMaxPayments', installment.nrMaxPayments ?? 0)
      setValue(
        'installment.firstInstallmentPercentage',
        installment.firstInstallmentPercentage,
      )
      setValue(
        'installment.firstInstallmentValue',
        installment.firstInstallmentValue,
      )
      setValue('installment.feePercentage', installment.feePercentage)
      setValue('installment.feeValue', installment.feeValue)
    },
    [setValue],
  )

  useEffect(() => {
    const temp = map(allLanguages, (lang) => {
      return {
        locale: lang.locale,
        name: '',
      }
    })
    setValue('content', temp)
    if (item) {
      setValue('content', item.nameTranslations)
      setValue('color', item.color.name)
      setValue('ordinal', item.ordinal)
      setValue('published', item.published)
      setValue(
        'publishDate',
        item.publishTimestamp
          ? formatDateTime(item.publishTimestamp)
          : formatDateTime(new Date().toISOString().substring(0, 16)),
      )
      if (item?.paymentsConfig?.paymentOption) {
        setValue(
          'paymentOption',
          find(
            ['BOTH', 'FULL_PRICE', 'MONTHLY'],
            (e) => e === item?.paymentsConfig?.paymentOption?.name,
          ),
        )
      }
      if (item.paymentsConfig?.maxPerOrder) {
        setValue('maxPerOrder', item.paymentsConfig?.maxPerOrder)
      }
      applyInstallmentDefaults(item.paymentsConfig?.installmentConfig)
    }

    return () => {
      reset()
    }
  }, [item, allLanguages, applyInstallmentDefaults, reset, setValue])

  const submit = async (values: IValues) => {
    const data: TicketCategoryPayload = {
      nameTranslations: values.content,
      color: find(colors, (color) => color.name === values.color),
      ordinal: values.ordinal,
      published: values.published,
      publishTimestamp: values.publishDate
        ? new Date(values.publishDate)?.getTime()
        : new Date().getTime(),
      paymentsConfig: {},
    }

    if (values.maxPerOrder && data.paymentsConfig) {
      data.paymentsConfig.maxPerOrder = values.maxPerOrder
    }
    if (values?.paymentOption && data.paymentsConfig) {
      data.paymentsConfig.paymentOption =
        find(
          paymentOptions,
          (option) => option.name === values?.paymentOption,
        ) || paymentOptions[0]
    }
    if (values?.installment && data.paymentsConfig) {
      data.paymentsConfig.installmentConfig = {
        installmentEndDate:
          !values?.installment?.nrMaxPayments && values?.installment?.endDate
            ? new Date(values.installment.endDate)?.valueOf()
            : null,
        nrMaxPayments:
          values?.installment?.nrMaxPayments !== 0
            ? values?.installment?.nrMaxPayments
            : null,
        firstInstallmentPercentage:
          values?.installment?.firstInstallmentPercentage !== 0
            ? values?.installment?.firstInstallmentPercentage
            : null,
        firstInstallmentValue:
          values?.installment?.firstInstallmentValue !== 0
            ? values?.installment?.firstInstallmentValue
            : null,
        feePercentage:
          values?.installment?.feePercentage !== 0
            ? values?.installment?.feePercentage
            : null,
        feeValue:
          values?.installment?.feeValue !== 0
            ? values?.installment?.feeValue
            : null,
      }
    }

    try {
      setSubmitting(true)
      const response = item
        ? await updateTicketCategory(item.id, { ...data, id: item.id })
        : await createTicketCategory(data)
      const categoriesResponse = await getAllCategories({ size: 30, sortBy })
      setTicketCategories(categoriesResponse.data.content)
      if (response.status === 200 && categoriesResponse.status === 200) {
        setIsError(false)
        setShowMessageBox(true)
        setMessage(item ? 'Update Successful' : 'Create Successful')
        handleClose()
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    } finally {
      setSubmitting(false)
    }
  }

  const modalTitle = useMemo(() => {
    const name = find(item?.nameTranslations, (i) => i.locale === 'en')
    return item ? `Edit ${name?.name ?? ''}` : 'Create New Ticket Category'
  }, [item])

  const SectionTitle = ({ children }: { children: React.ReactNode }) => (
    <div className="mb-5 text-2xl font-bold leading-none text-monochrome-1">
      {children}
    </div>
  )

  return (
    <>
      <Modal
        open={visible}
        onOpenChange={(open) => {
          if (!open) handleClose()
        }}
        size="xl"
        title={
          <div className="border-b border-monochrome-120 pb-4">
            <PageTitle title="Ticket Categories" subtitle={modalTitle} />
          </div>
        }
        footer={
          <div className="flex w-full items-center gap-2 justify-end">
            <Button onClick={handleClose} type="button" variant="secondary">
              Cancel
            </Button>
            <Button
              disabled={
                loading || submitting || Object.keys(dirtyFields).length === 0
              }
              form="stage-form"
              type="submit"
              variant="primary"
              loading={submitting}
            >
              Save
            </Button>
          </div>
        }
      >
        <SectionTitle>Main info</SectionTitle>
        <form
          id="stage-form"
          onSubmit={handleSubmit(submit)}
          className="space-y-7"
        >
          <div className="flex flex-col gap-2">
            {map(allLanguages, (lang, index) => {
              return (
                <div
                  className={cn(
                    'flex w-full flex-col gap-3 rounded-[10px] border border-monochrome-120 bg-monochrome-5-50 p-4',
                  )}
                  key={lang.id}
                >
                  <FormGroup label="Language:" wrapperClassName="mb-0">
                    <span className="text-sm font-semibold text-accent-1">
                      {lang.name}
                    </span>
                  </FormGroup>
                  <FormGroup
                    label="Category Name"
                    tooltip={translate(
                      language.value,
                      'tickets.categories.categoryNameTooltip',
                    )}
                    error={errors?.content?.[index]?.name?.message}
                  >
                    <Input
                      prefix={<img src={CategoryIcon} alt="" />}
                      isInvalid={!!errors?.content?.[index]?.name?.message}
                      {...register(`content.${index}.name`)}
                      type="text"
                      placeholder="Enter Category Name"
                    />
                  </FormGroup>
                </div>
              )
            })}
          </div>

          <FormGroup
            label="Category Tickets Color"
            tooltip={translate(
              language.value,
              'tickets.categories.categoryTicketColorTooltip',
            )}
            error={errors?.color?.message}
          >
            <Controller
              name="color"
              control={control}
              render={({ field }) => (
                <div className="relative">
                  <img
                    src={ColorIcon}
                    alt=""
                    className="absolute left-3 top-1/2 -translate-y-1/2 z-10 pointer-events-none w-6 h-6"
                  />
                  <SelectField
                    outline
                    triggerClassName="pl-12"
                    error={errors?.color?.message}
                    placeholder="Select Color"
                    value={field.value}
                    onValueChange={field.onChange}
                    options={map(colors, (color) => ({
                      value: color.name,
                      label: (
                        <div className="flex items-center gap-2">
                          <ColorCircle gradient={color} />
                          <span>{color.name}</span>
                        </div>
                      ),
                    }))}
                  />
                </div>
              )}
            />
          </FormGroup>

          <FormGroup
            label="Category Ordinal"
            tooltip={translate(
              language.value,
              'tickets.categories.categoryOrdinalTooltip',
            )}
            error={errors?.ordinal?.message}
          >
            <Input
              prefix={<img src={TicketOrdinal} alt="" />}
              isInvalid={!!errors?.ordinal?.message}
              {...register(`ordinal`)}
              type="number"
              placeholder="Enter Ordinal"
            />
          </FormGroup>

          <FormGroup
            label="Max Tickets per Order"
            tooltip={translate(
              language.value,
              'tickets.categories.maxTicketsPerOrderTooltip',
            )}
            error={errors?.maxPerOrder?.message}
          >
            <Input
              prefix={<img src={MaxNumberIcon} alt="" />}
              isInvalid={!!errors?.maxPerOrder?.message}
              {...register('maxPerOrder')}
              type="number"
              min={0}
              placeholder="Enter Max Tickets per Order"
            />
          </FormGroup>

          <div className="my-4 h-px w-full bg-monochrome-120" />

          <SectionTitle>Payment Options</SectionTitle>

          <Controller
            name="paymentOption"
            control={control}
            render={({ field }) => (
              <SelectField
                outline
                error={errors?.paymentOption?.message}
                placeholder="Select Payment Option"
                value={field.value}
                onValueChange={field.onChange}
                options={[
                  {
                    value: 'BOTH',
                    label: 'Both (Full Price, Monthly Payments)',
                  },
                  { value: 'FULL_PRICE', label: 'Full Price' },
                  { value: 'MONTHLY', label: 'Monthly Payments' },
                ]}
              />
            )}
          />

          <div className="my-4 h-px w-full bg-monochrome-120" />

          <SectionTitle>Monthly Payment (default settings)</SectionTitle>

          <div className="flex items-end gap-3">
            <div className="flex flex-1 flex-col gap-3">
              <FormGroup
                label="End Date (default settings)"
                tooltip={translate(
                  language.value,
                  'tickets.categories.monthlyPayments.endDateTooltip',
                )}
                error={errors?.installment?.endDate?.message}
              >
                <Controller
                  name="installment.endDate"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <DateTimePicker
                      value={value ? new Date(value) : null}
                      onChange={(date) => {
                        onChange(date ? formatDateTime(date.toISOString()) : '')
                        setValue('installment.nrMaxPayments', 0, {
                          shouldValidate: true,
                        })
                      }}
                      placeholder="Select Date"
                      hasError={!!errors?.installment?.endDate?.message}
                    />
                  )}
                />
              </FormGroup>
            </div>
            <span className="mb-2 text-monochrome-2">or</span>
            <div className="flex flex-1 flex-col gap-3">
              <FormGroup
                label="Max Payments (default settings)"
                tooltip={translate(
                  language.value,
                  'tickets.categories.monthlyPayments.maxPaymentsTooltip',
                )}
                error={errors?.installment?.nrMaxPayments?.message}
              >
                <Controller
                  name="installment.nrMaxPayments"
                  control={control}
                  render={({ field }) => (
                    <SelectField
                      outline
                      error={errors?.installment?.nrMaxPayments?.message}
                      placeholder="Select Max Payments"
                      value={field.value?.toString() || '0'}
                      onValueChange={(value) => {
                        field.onChange(Number(value))
                        setValue('installment.endDate', undefined, {
                          shouldValidate: true,
                        })
                      }}
                      options={map(
                        [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
                        (n) => ({
                          value: n.toString(),
                          label: n.toString(),
                        }),
                      )}
                    />
                  )}
                />
              </FormGroup>
            </div>
          </div>

          <div className="flex items-end gap-3">
            <div className="flex flex-1 flex-col gap-3">
              <FormGroup
                label="First Rate in % (default settings)"
                tooltip={translate(
                  language.value,
                  'tickets.categories.monthlyPayments.firstPaymentPercentageTooltip',
                )}
                error={errors?.installment?.firstInstallmentPercentage?.message}
              >
                <Input
                  type="number"
                  min={0}
                  max={100}
                  placeholder="Enter %"
                  isInvalid={
                    !!errors?.installment?.firstInstallmentPercentage?.message
                  }
                  {...register('installment.firstInstallmentPercentage')}
                  onChange={(e) => {
                    register('installment.firstInstallmentPercentage').onChange(
                      e,
                    )
                    setValue('installment.firstInstallmentValue', undefined, {
                      shouldValidate: true,
                    })
                  }}
                />
              </FormGroup>
            </div>
            <span className="mb-2 text-monochrome-2">or</span>
            <div className="flex flex-1 flex-col gap-3">
              <FormGroup
                label={`First Rate in ${currency} (default settings)`}
                tooltip={translate(
                  language.value,
                  'tickets.categories.monthlyPayments.firstRateValueTooltip',
                  { currency },
                )}
                error={errors?.installment?.firstInstallmentValue?.message}
              >
                <Input
                  type="number"
                  min={0}
                  placeholder="Enter Amount"
                  isInvalid={
                    !!errors?.installment?.firstInstallmentValue?.message
                  }
                  {...register('installment.firstInstallmentValue')}
                  onChange={(e) => {
                    register('installment.firstInstallmentValue').onChange(e)
                    setValue(
                      'installment.firstInstallmentPercentage',
                      undefined,
                      {
                        shouldValidate: true,
                      },
                    )
                  }}
                />
              </FormGroup>
            </div>
          </div>

          <div className="flex items-end gap-3">
            <div className="flex flex-1 flex-col gap-3">
              <FormGroup
                label="Fee in % (default settings)"
                tooltip={translate(
                  language.value,
                  'tickets.categories.monthlyPayments.feeInPercentageTooltip',
                )}
                error={errors?.installment?.feePercentage?.message}
              >
                <Input
                  type="number"
                  min={0}
                  max={100}
                  placeholder="Enter %"
                  isInvalid={!!errors?.installment?.feePercentage?.message}
                  {...register('installment.feePercentage')}
                  onChange={(e) => {
                    register('installment.feePercentage').onChange(e)
                    setValue('installment.feeValue', undefined, {
                      shouldValidate: true,
                    })
                  }}
                />
              </FormGroup>
            </div>
            <span className="mb-2 text-monochrome-2">or</span>
            <div className="flex flex-1 flex-col gap-3">
              <FormGroup
                label={`Fee in ${currency} (default settings)`}
                tooltip={translate(
                  language.value,
                  'tickets.categories.monthlyPayments.feeInValueTooltip',
                  { currency },
                )}
                error={errors?.installment?.feeValue?.message}
              >
                <Input
                  type="number"
                  placeholder={`Enter Fee in ${currency}`}
                  min={0}
                  isInvalid={!!errors?.installment?.feeValue?.message}
                  {...register('installment.feeValue')}
                  onChange={(e) => {
                    register('installment.feeValue').onChange(e)
                    setValue('installment.feePercentage', undefined, {
                      shouldValidate: true,
                    })
                  }}
                />
              </FormGroup>
            </div>
          </div>

          <div className="my-4 h-px w-full bg-monochrome-120" />

          <FormGroup
            label="Publish Date (default settings)"
            tooltip={translate(
              language.value,
              'tickets.categories.publishDateTooltip',
            )}
            error={errors?.publishDate?.message}
          >
            <DateTimePicker
              value={
                watch('publishDate')
                  ? new Date(watch('publishDate') as string)
                  : null
              }
              onChange={(date) => {
                setValue(
                  'publishDate',
                  date ? formatDateTime(date.toISOString()) : '',
                )
              }}
              placeholder="Enter Date"
              hasError={!!errors?.publishDate?.message}
            />
          </FormGroup>

          <FormGroup
            label="Published"
            direction="row"
            labelClassName="whitespace-nowrap"
          >
            <Controller
              name="published"
              control={control}
              render={({ field }) => (
                <Checkbox
                  checked={Boolean(field.value)}
                  onCheckedChange={(checked) =>
                    field.onChange(Boolean(checked))
                  }
                />
              )}
            />
          </FormGroup>
        </form>
      </Modal>
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
      />
    </>
  )
}
